import { useEffect, useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { useLoad } from '../../data';
import { Loading, MoneyField, PageHead, Panel, fmtMoney } from '../../ui';
import { Onboarding, Product } from '../../types';
import { SETUP_NEXT, TRIP_FORM, perWords, readBack, saveTripLabel, tripOffer } from '../../joining';
import { chosenMinor, moneyInText, priceBoxText, readGuests, readHours } from '../../price';
import { nextSetupPath } from './setupNext';

// The supplier's OWN products (canon 11 E2 step 4 "First product", and the
// "Add your other trips" task afterwards). The supplier writes their own fields
// through POST/PUT /v1/products — gross price, no margin (DECISION-015/016);
// Eva's upsert_product is the SAME operation. This is a supplier-scoped route
// reusing the Form template (720 centred, sticky footer) — not a new registered
// rail screen (the register stays closed at 41; George's S11 ruling).
//
// N39: per person or for the whole boat or car is asked, never assumed (nothing
// starts selected); the price box reads a comma as the decimal point and asks
// about "1.200" (price.ts, the API's rule); Save shows the whole figure; while
// setting up, a first trip can start from the inviting hotel's, and Save leads
// to the next step. After setup My trips is reached from a link on Today and
// on Business profile — never a rail entry (DECISION-054).

const KINDS: { key: 'experience' | 'transfer'; label: string }[] = [
  { key: 'experience', label: 'Experience' },
  { key: 'transfer', label: 'Transfer' },
];

export function Trips({ editId }: { editId?: string }) {
  const { data, error, reload } = useLoad<{ items: Product[] }>('/products');
  const items = data ? data.items : null;
  if (editId) return <Editor items={items} loadError={error} editId={editId} onSaved={reload} />;
  return <List items={items} loadError={error} />;
}

function List({ items, loadError }: { items: Product[] | null; loadError: string }) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title="My trips" sub="What you sell — the hotels you connect with book these." />

      {loadError && <p className="error-line">{loadError}</p>}
      {items === null ? (
        <Loading />
      ) : (
        <Panel title="Your trips" meta={items.length ? `${items.length}` : ''} flush>
          {items.length === 0 ? (
            <div className="card-body">
              {/* B35: not "so hotels can book you" — a trip is what a hotel lists, and
                  only a listed trip can be booked. */}
              <div className="empty-neutral">No trips yet — add your first. A hotel can only list what is here.</div>
            </div>
          ) : (
            <table>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="click" onClick={() => go(`/trips/${p.id}`)}>
                    <td>
                      {p.name}
                      <br />
                      <span className="hint">
                        {p.kind === 'transfer' ? 'Transfer' : 'Experience'}
                        {p.durationHours ? ` · ${p.durationHours} h` : ''}
                        {p.maxGuests ? ` · max ${p.maxGuests}` : ''}
                      </span>
                    </td>
                    <td className="num mono">
                      {fmtMoney(p.supplierPrice)}
                      <br />
                      <span className="hint">{perWords(p.per)}</span>
                    </td>
                    <td className="num"><span className="open">Edit →</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      <div className="form-foot">
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => go('/trips/new')} style={{ flex: 'none', minWidth: 160 }}>
          Add a trip
        </button>
      </div>
    </div>
  );
}

function Editor({
  items,
  loadError,
  editId,
  onSaved,
}: {
  items: Product[] | null;
  loadError: string;
  editId: string;
  onSaved: () => Promise<void>;
}) {
  const isNew = editId === 'new';
  const existing = isNew ? null : items?.find((p) => p.id === editId) ?? null;
  // N39: while setting up, Save leads to the next step, and a first trip can
  // start from the trip the inviting hotel described (DECISION-080 §7).
  const ob = useLoad<Onboarding>('/onboarding');
  const settingUp = ob.data?.status === 'onboarding';
  const offer = isNew && settingUp && items !== null && items.length === 0 ? ob.data?.invitedTrip : undefined;

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'experience' | 'transfer'>('experience');
  // Asked, never assumed (N39): a new trip starts with neither selected.
  const [per, setPer] = useState<'guest' | 'job' | undefined>(undefined);
  const [priceStr, setPriceStr] = useState('');
  const [choice, setChoice] = useState<number | null>(null);
  const [durationStr, setDurationStr] = useState('');
  const [maxStr, setMaxStr] = useState('');
  const [seeded, setSeeded] = useState(isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Seed the form from the existing product once it has loaded.
  useEffect(() => {
    if (!seeded && existing) {
      setName(existing.name);
      setKind(existing.kind);
      setPer(existing.per);
      setPriceStr(priceBoxText(existing.supplierPrice.minor));
      setDurationStr(existing.durationHours != null ? String(existing.durationHours) : '');
      setMaxStr(existing.maxGuests != null ? String(existing.maxGuests) : '');
      setSeeded(true);
    }
  }, [existing, seeded]);

  // Use it: the hotel's trip as it described it — the same name, price and per
  // what, so Finish can list it with that hotel. Saving is still the supplier's tap.
  const takeOffer = () => {
    if (!offer) return;
    const t = offer.trip;
    setName(t.name);
    setKind(t.kind);
    setPer(t.per);
    setPriceStr(priceBoxText(t.supplierPrice.minor));
    setChoice(null);
    setDurationStr(t.durationHours != null ? String(t.durationHours) : '');
    setMaxStr(t.maxGuests != null ? String(t.maxGuests) : '');
  };

  const back = () => go('/trips');
  const currency = existing?.supplierPrice.currency ?? offer?.trip.supplierPrice.currency ?? 'EUR';
  // In running text and on the button, "€" and the digits never part.
  const money = (minor: number) => moneyInText(minor, currency);
  // The price box reads a comma as the decimal point and asks about "1.200"
  // (price.ts — the API's rule, through MoneyField); never Number.parseFloat,
  // which made "58,50" € 58.
  const priceMinor = chosenMinor(priceStr, choice);
  const priceOk = priceMinor !== undefined && priceMinor >= 0;
  // A new trip asks how long it takes and how many it takes, as the phone does
  // (N39 review): DECISION-074's over-capacity refusal then covers trips made at
  // the desk. An older trip's edit keeps them optional; the API takes either.
  const needFacts = isNew;
  const hours = durationStr.trim() ? readHours(durationStr) : undefined;
  const hoursOk = durationStr.trim() ? hours !== undefined : !needFacts;
  const guests = maxStr.trim() ? readGuests(maxStr) : undefined;
  const guestsOk = maxStr.trim() ? guests !== undefined : !needFacts;
  const nameOk = name.trim().length > 0 && name.trim().length <= 120;
  const canSave = nameOk && priceOk && !!per && hoursOk && guestsOk;

  const save = async () => {
    if (!canSave || priceMinor === undefined || !per) {
      setErr(
        !nameOk
          ? 'A name is needed.'
          : !priceOk
            ? TRIP_FORM.priceBad
            : !per
              ? TRIP_FORM.perAsk
              : !hoursOk
                ? durationStr.trim() ? TRIP_FORM.durationBad : TRIP_FORM.durationNeeded
                : maxStr.trim() ? TRIP_FORM.guestsBad : TRIP_FORM.guestsNeeded,
      );
      return;
    }
    setBusy(true);
    setErr('');
    const body: Record<string, unknown> = {
      name: name.trim(),
      kind,
      per,
      supplierPrice: { minor: priceMinor, currency },
    };
    if (hours !== undefined) body.durationHours = hours;
    if (guests !== undefined) body.maxGuests = guests;
    try {
      if (isNew) await api('POST', '/products', body);
      else await api('PUT', `/products/${editId}`, body);
      await onSaved();
      // Each setup form leads to the next while setting up (N39).
      const next = await nextSetupPath();
      if (next) go(next);
      else back();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  if (!isNew && items === null) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <PageHead title="Edit trip" />
        {loadError ? <p className="error-line">{loadError}</p> : <Loading />}
      </div>
    );
  }
  if (!isNew && !existing) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <PageHead title="Edit trip" />
        <div className="empty-neutral">No such trip. <a href="#/trips">Back to my trips</a></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title={isNew ? 'Add a trip' : 'Edit trip'} sub="Your figure is gross — the hotel adds its own margin on top." />

      {offer && (
        <Panel title={offer.hotelName}>
          <p style={{ margin: 0 }}>{tripOffer(offer.hotelName, offer.trip, money(offer.trip.supplierPrice.minor))}</p>
          {offer.listsIfKept && <p className="hint" style={{ marginTop: 'var(--space-sm)' }}>{TRIP_FORM.keepListed(offer.hotelName)}</p>}
          <button type="button" className="btn secondary" onClick={takeOffer} style={{ marginTop: 'var(--space-md)' }}>
            {TRIP_FORM.useIt}
          </button>
        </Panel>
      )}

      <Panel title="The trip">
        <div className="field">
          <label htmlFor="tname">Name</label>
          <input id="tname" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label>Kind</label>
          <div>
            {KINDS.map((k) => (
              <button key={k.key} type="button" className={`reason ${kind === k.key ? 'selected' : ''}`} onClick={() => setKind(k.key)}>
                {k.label}
                {kind === k.key && <span className="disc" />}
              </button>
            ))}
          </div>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <MoneyField
            id="tprice"
            label={TRIP_FORM.priceLabel}
            currency={currency}
            placeholder="58,50"
            bad={TRIP_FORM.priceBad}
            value={priceStr}
            choice={choice}
            onChange={(v) => {
              setPriceStr(v);
              setChoice(null);
            }}
            onChoose={setChoice}
          />
          <div className="field">
            <label>{TRIP_FORM.per}</label>
            <div role="radiogroup" aria-label={TRIP_FORM.per}>
              {(['guest', 'job'] as const).map((p) => (
                <button key={p} type="button" role="radio" className={`reason ${per === p ? 'selected' : ''}`} onClick={() => setPer(p)} aria-checked={per === p}>
                  {p === 'guest' ? TRIP_FORM.perGuest : TRIP_FORM.perJob}
                  {per === p && <span className="disc" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="tdur">{TRIP_FORM.duration(needFacts)}</label>
            <input id="tdur" className="mono" inputMode="decimal" value={durationStr} onChange={(e) => setDurationStr(e.target.value)} />
            {durationStr.trim() !== '' && !hoursOk && <p className="error-line">{TRIP_FORM.durationBad}</p>}
          </div>
          <div className="field">
            <label htmlFor="tmax">{TRIP_FORM.guests(needFacts)}</label>
            <input id="tmax" className="mono" inputMode="numeric" value={maxStr} onChange={(e) => setMaxStr(e.target.value)} />
            {maxStr.trim() !== '' && !guestsOk && <p className="error-line">{TRIP_FORM.guestsBad}</p>}
          </div>
        </div>
        {/* The figure read back, with an example — integer minor units; only the
            figure is set in mono (prose stays in the body face). */}
        {priceMinor !== undefined && priceMinor > 0 && per && (() => {
          const r = readBack(priceMinor, per, kind, guests, money);
          return (
            <p className="hint" role="status">
              {r.lead}
              <span className="mono">{r.figure}</span>
              {r.tail}
            </p>
          );
        })()}
        {err && <p className="error-line" role="alert">{err}</p>}
      </Panel>

      <div className="form-foot">
        <span className="hint" style={{ flex: 1, alignSelf: 'center' }}>{settingUp ? SETUP_NEXT.thenNext : ' '}</span>
        <button className="btn secondary" onClick={back} style={{ flex: 'none' }} disabled={busy}>Cancel</button>
        <button className="btn" onClick={save} style={{ flex: 'none', minWidth: 200 }} disabled={busy || !canSave}>
          {busy ? 'Saving…' : saveTripLabel(isNew, priceMinor !== undefined && priceMinor > 0 ? money(priceMinor) : undefined, per)}
        </button>
      </div>
    </div>
  );
}
